import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Text, ButtonGroup, DaButton, LeafIcon } from '@paygreen/paygreen-ui';
import { useMode } from '../../../context';
import {
    ModalForm,
    DeleteButton,
    SimpleActionButton,
    Loader,
    Message,
} from '../../../components';
import { actionProductModes } from '../../../utils/constants';
import { patchGroupedMatchingProducts } from '../service';
import GroupedProductsList from './GroupedProductsList';
import GroupedMatching from './GroupedMatching';

const GroupedProductsModal = ({
    selectedRows,
    productsData,
    mode: actionMode,
    handleRefreshData,
    setOpen,
    isOpen,
    t,
}) => {
    const { mode } = useMode();
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [displayMatching, setDisplayMatching] = useState(false);

    const isDeleteMode = actionMode === actionProductModes.delete;
    const isMatchingMode = actionMode === actionProductModes.match;

    const cancelCloseModal = () => {
        setDisplayMatching(false);
        setOpen(false);
        setErrorMessage();
    };

    /**
     * @description generate object to send to API to unmatch/delete a group of products
     * @param {array} selectedRows
     * @param {array} productsData
     * @param {bool} withProductName
     */
    const updatedProducts = (selectedRows, productsData, withProductName) => {
        let newObj = [
            ...selectedRows?.map((item) => {
                let action;
                if (isDeleteMode) {
                    action = { isArchived: 1 };
                } else if (isMatchingMode) {
                    action = { isArchived: 1 };
                }

                const productName = withProductName
                    ? { productName: productsData[Number(item)]?.productName }
                    : null;

                return {
                    productExternalReference:
                        productsData[Number(item)]?.productExternalReference,
                    ...action,
                    ...productName,
                };
            }),
        ];

        return newObj;
    };

    /**
     * Patch request to API to update/delete a group of products
     */
    const updateGroupedProducts = () => {
        setIsLoading(true);
        setErrorMessage();

        patchGroupedMatchingProducts(
            updatedProducts(selectedRows, productsData),
            mode,
        )
            .then(() => {
                setOpen(false);

                handleRefreshData(true);

                !isLoading &&
                    toast(
                        <Text colorPallet="theme" textSize="xs">
                            {t('form.toastSuccess.deleteProducts')}
                        </Text>,
                    );
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;

                setErrorMessage(status + ' / ' + detail);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleMatching = () => {
        setDisplayMatching(true);
    };

    return (
        <ModalForm
            modalTitle={
                isDeleteMode
                    ? t('matching.grouped.deleteButton')
                    : t('matching.grouped.matchButton')
            }
            blockWidth="lg"
            isOpen={isOpen}
            isLoading={isLoading}
            cancelCloseModal={cancelCloseModal}
        >
            {!displayMatching && (
                <>
                    <GroupedProductsList
                        mode={actionMode}
                        productsData={updatedProducts(
                            selectedRows,
                            productsData,
                            true,
                        )}
                    />

                    {errorMessage && (
                        <Message
                            message={errorMessage}
                            type="error"
                            paddingLateral="sm"
                            marginTop="md"
                            marginBottom="xs"
                            blockWidth="lg"
                            hasStaticWidth={true}
                        />
                    )}

                    <ButtonGroup marginTop="sm" marginBottom="xs">
                        <SimpleActionButton
                            buttonSize="md"
                            icon="ArrowRoundTopLeftIcon"
                            onClick={cancelCloseModal}
                        >
                            {t('form.button.cancel')}
                        </SimpleActionButton>

                        {isMatchingMode && (
                            <button onClick={handleMatching}>
                                <DaButton
                                    buttonSize="md"
                                    colorPallet="status"
                                    colorStatus="success"
                                    icon={<LeafIcon />}
                                >
                                    {t('matching.grouped.matchButton')}
                                </DaButton>
                            </button>
                        )}
                        {isDeleteMode && (
                            <DeleteButton
                                isDisabled={isLoading}
                                buttonSize="md"
                                onClick={updateGroupedProducts}
                                icon="TrashIcon"
                            >
                                {t('matching.grouped.deleteButton')}
                            </DeleteButton>
                        )}
                    </ButtonGroup>
                </>
            )}
            {displayMatching && (
                <GroupedMatching
                    productsData={updatedProducts(
                        selectedRows,
                        productsData,
                        true,
                    )}
                    setIsLoading={setIsLoading}
                    handleRefreshData={handleRefreshData}
                    setOpen={setOpen}
                    setDisplayMatching={setDisplayMatching}
                    setErrorMessage={setErrorMessage}
                />
            )}

            {/* loader when updating and closing modal */}
            {isLoading && <Loader isLoading={isLoading} />}
        </ModalForm>
    );
};

GroupedProductsModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    productsData: PropTypes.array,
    handleRefreshData: PropTypes.func,
    mode: PropTypes.oneOf(Object.values(actionProductModes)),
};

GroupedProductsModal.defaultProps = {
    isOpen: false,
    mode: actionProductModes.delete,
};

export default withTranslation('common')(GroupedProductsModal);
